import type { LineNumber } from '../../api/monitor';
import { NavIcon } from '../../components/layout/NavIcon';
import { formatLine, formatUsage, lineOwner, usageHint } from '../../lib/lines';
import { operatorPill } from '../../lib/operators';

/**
 * A etiqueta da situação da linha, como o SGC a vê. O motivo (cancelada quando,
 * estoque, fora do portal) vai na dica, para a coluna não crescer.
 */
const SITUATION_BADGE: Record<LineNumber['situation'], { label: string; className: string }> = {
  active: { label: 'Ativa', className: 'bg-ok-100 text-ok-600' },
  unassigned: { label: 'Sem cliente', className: 'bg-warn-100 text-warn-700' },
  inactive: { label: 'Inativa', className: 'bg-danger-100 text-danger-600' },
};

/** O mesmo ícone circular de "sincronizar" do menu de serviços assíncronos. */
const SYNC_ICON = 'M20 12a8 8 0 1 1-2.3-5.6M20 3v4h-4';

interface LineRowProps {
  number: LineNumber;
  /** Esta linha está sendo sincronizada agora. */
  syncing: boolean;
  /** Qualquer linha está sendo sincronizada — trava as demais. */
  busy: boolean;
  onSync: (number: LineNumber) => void;
  /** Abre o side sheet de detalhes da linha. */
  onOpen: () => void;
}

/** Uma linha da listagem: número, dono, consumo e o atalho de sincronizar. */
export function LineRow({ number, syncing, busy, onSync, onOpen }: LineRowProps) {
  const formatted = formatLine(number.line);
  const usage = formatUsage(number.consumption);
  const hint = usageHint(number.consumption);

  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className="border-surface-200 hover:bg-surface-50 focus-visible:ring-[#00244733] flex cursor-pointer items-center gap-2.5 border-b px-4 py-3 last:border-b-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:outline-none"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-extrabold">{formatted}</span>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${operatorPill(number.operator.code)}`}
          >
            {number.operator.name}
          </span>
        </div>
        <div className="text-ink-600 mt-0.5 truncate text-[12px]">{lineOwner(number)}</div>
      </div>

      <div className="shrink-0 text-right">
        {/* Sem consumo importado a coluna fica vazia, e não zerada: 0 GB é um dado. */}
        {usage ? (
          <div className="text-[12px] font-bold" title={hint ?? undefined}>
            {usage}
          </div>
        ) : null}
        <div
          className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10.5px] font-bold ${
            SITUATION_BADGE[number.situation].className
          }`}
          title={number.situationReason ?? undefined}
        >
          {SITUATION_BADGE[number.situation].label}
        </div>
      </div>

      {/* Uma linha por vez: cada sincronização da Vivo abre uma sessão no
          portal, e o portal derruba a anterior quando a mesma conta entra de
          novo — duas ao mesmo tempo atrapalhariam uma à outra. */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onSync(number);
        }}
        disabled={busy}
        title={
          syncing
            ? `Sincronizando a linha ${formatted}…`
            : busy
              ? 'Outra linha está sendo sincronizada.'
              : `Sincronizar a linha ${formatted}`
        }
        className="border-line-300 text-navy-800 hover:border-navy-800 hover:bg-surface-100 disabled:hover:border-line-300 shrink-0 rounded-lg border bg-white p-[7px] disabled:opacity-40"
      >
        <NavIcon path={SYNC_ICON} size={15} className={syncing ? 'animate-spin' : undefined} />
        <span className="sr-only">Sincronizar a linha {formatted}</span>
      </button>
    </div>
  );
}
